import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'react-toastify';
import workAssignmentService, { Holiday } from '../services/workAssignment.service';
import JalaliDatePicker, { toPersianDigits } from '../components/common/JalaliDatePicker';
import { ConfirmDialog } from '../components/common';
import moment from 'moment-jalaali';

import './EntityManagement.css';

const defaultForm: Partial<Holiday> = {
  date: new Date().toISOString().slice(0, 10),
  nameFa: '',
  type: 'official',
  isRecurring: false,
  isActive: true,
};

const typeLabels: Record<string, string> = {
  official: 'رسمی',
  religious: 'مذهبی',
  custom: 'سفارشی',
};

const typeTagClass: Record<string, string> = {
  official: 'tag-admin',
  religious: 'tag-platform',
  custom: 'tag-warning',
};

const HolidayManagement: React.FC = () => {
  const [rows, setRows] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Dialogs
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Holiday | undefined>();
  const [form, setForm] = useState<Partial<Holiday>>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Holiday | undefined>();

  const load = async () => {
    setLoading(true);
    try {
      const data = await workAssignmentService.listHolidays();
      setRows(data || []);
    } catch (e: any) {
      toast.error(e?.message || 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (editorOpen) {
      if (editing) {
        setForm({ ...editing, date: editing.date?.slice(0, 10) });
      } else {
        setForm(defaultForm);
      }
      setFormError('');
    }
  }, [editing, editorOpen]);

  const handleSearch = () => { setSearchTerm(searchInput); setPage(1); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };
  const clearFilters = () => {
    setSearchTerm(''); setSearchInput(''); setTypeFilter(''); setPage(1);
  };

  const openCreate = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };

  const openEdit = (row: Holiday) => {
    setEditing(row);
    setEditorOpen(true);
  };

  const closeEditor = () => { setEditorOpen(false); setEditing(undefined); };

  const save = async () => {
    if (!form.nameFa) { setFormError('نام تعطیلی الزامی است'); return; }
    if (form.isRecurring && (!form.jalaliMonth || !form.jalaliDay)) {
      setFormError('برای تعطیلی بازگشتی، ماه و روز شمسی الزامی است');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await workAssignmentService.updateHoliday(editing._id, form);
      } else {
        await workAssignmentService.createHoliday(form);
      }
      toast.success('ذخیره شد');
      closeEditor();
      load();
    } catch (e: any) {
      setFormError(e?.message || 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row: Holiday) => { setToDelete(row); setDeleteOpen(true); };
  const closeDelete = () => { setDeleteOpen(false); setToDelete(undefined); };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await workAssignmentService.deleteHoliday(toDelete._id);
      toast.success('حذف شد');
      closeDelete();
      load();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در حذف');
    } finally {
      setSaving(false);
    }
  };

  const filtered = rows.filter(r => {
    if (searchTerm && !r.nameFa?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (typeFilter && r.type !== typeFilter) return false;
    return true;
  });

  const totalCount = filtered.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const hasActiveFilters = searchTerm || typeFilter;

  const activeCount = rows.filter(r => r.isActive).length;
  const recurringCount = rows.filter(r => r.isRecurring).length;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تقویم تعطیلات</h1>
          <p className="entity-subtitle">مدیریت روزهای تعطیل رسمی، مذهبی و سفارشی</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={load} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={openCreate}>
            <AddIcon fontSize="small" />
            تعطیلی جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><EventIcon /></div>
          <div>
            <p className="entity-stat-label">کل تعطیلات</p>
            <p className="entity-stat-value">{rows.length}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple"><RepeatIcon /></div>
          <div>
            <p className="entity-stat-label">بازگشتی سالانه</p>
            <p className="entity-stat-value">{recurringCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع تعطیلی</label>
            <select
              className="filter-field"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه انواع</option>
              <option value="official">رسمی</option>
              <option value="religious">مذهبی</option>
              <option value="custom">سفارشی</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {typeFilter && (
              <span className="filter-tag">
                نوع: {typeLabels[typeFilter]}
                <button onClick={() => setTypeFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : paginated.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><EventIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">موردی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>نام تعطیلی</th>
                  <th>نوع</th>
                  <th>تاریخ</th>
                  <th>وضعیت</th>
                  <th style={{ width: 120 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image"><EventIcon /></div>
                        <div className="entity-info">
                          <span className="entity-name">{row.nameFa}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`tag tag-sm ${typeTagClass[row.type] || 'tag-admin'}`}>
                        {typeLabels[row.type] || row.type}
                      </span>
                    </td>
                    <td>
                      {row.isRecurring ? (
                        <span className="tag tag-sm tag-platform">
                          <RepeatIcon fontSize="inherit" />
                          {' '}
                          {toPersianDigits(`${row.jalaliMonth}/${row.jalaliDay}`)} (سالانه)
                        </span>
                      ) : (
                        <span className="cell-code">
                          {row.date ? toPersianDigits(moment(row.date).format('jYYYY/jMM/jDD')) : '-'}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`tag tag-sm ${row.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {row.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => openEdit(row)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="حذف" arrow>
                          <button className="action-btn delete" onClick={() => openDelete(row)}>
                            <DeleteIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {paginated.map((row) => (
                <div key={row._id} className={`entity-card ${!row.isActive ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image"><EventIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{row.nameFa}</div>
                      <div className="entity-card-desc">
                        {typeLabels[row.type]} •{' '}
                        {row.isRecurring
                          ? `${toPersianDigits(`${row.jalaliMonth}/${row.jalaliDay}`)} سالانه`
                          : row.date ? toPersianDigits(moment(row.date).format('jYYYY/jMM/jDD')) : '-'}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${row.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {row.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn danger" onClick={() => openDelete(row)}>
                      <DeleteIcon fontSize="small" /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={editorOpen} onClose={closeEditor} maxWidth="sm" fullWidth className="holiday-edit-dialog">
        <div className="edit-dialog-title">
          <span>{editing ? 'ویرایش تعطیلی' : 'تعطیلی جدید'}</span>
          <button className="details-close" onClick={closeEditor}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic Info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>نام تعطیلی <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={form.nameFa || ''}
                      onChange={(e) => setForm({ ...form, nameFa: e.target.value })}
                      placeholder="مثلا: نوروز"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>نوع</label>
                    <select
                      value={form.type || 'official'}
                      onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                    >
                      <option value="official">رسمی</option>
                      <option value="religious">مذهبی</option>
                      <option value="custom">سفارشی</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Date */}
            <div className="edit-card">
              <div className="edit-card-header">تاریخ</div>
              <div className="edit-card-body">
                <div className="edit-switches" style={{ marginBottom: 12 }}>
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">بازگشتی سالانه</div>
                      <div className="edit-switch-desc">بر اساس تقویم شمسی (ماه و روز)</div>
                    </div>
                    <Switch size="small" checked={!!form.isRecurring}
                      onChange={(_, v) => setForm({ ...form, isRecurring: v })} />
                  </label>
                </div>
                {form.isRecurring ? (
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>ماه شمسی <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={form.jalaliMonth ?? ''}
                        onChange={(e) => setForm({ ...form, jalaliMonth: Number(e.target.value) || undefined })}
                        placeholder="1 تا 12"
                      />
                    </div>
                    <div className="edit-field">
                      <label>روز شمسی <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={form.jalaliDay ?? ''}
                        onChange={(e) => setForm({ ...form, jalaliDay: Number(e.target.value) || undefined })}
                        placeholder="1 تا 31"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>تاریخ</label>
                      <JalaliDatePicker
                        value={form.date || ''}
                        onChange={(iso) => setForm({ ...form, date: iso.slice(0, 10) })}
                        outputDateOnly
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت فعال</div>
                      <div className="edit-switch-desc">این تعطیلی در تقویم اعمال شود</div>
                    </div>
                    <Switch size="small" checked={!!form.isActive}
                      onChange={(_, v) => setForm({ ...form, isActive: v })} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeEditor}>انصراف</button>
          <button className="edit-btn submit" onClick={save} disabled={saving}>
            {saving && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {editing ? 'ذخیره تغییرات' : 'افزودن تعطیلی'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف تعطیلی"
        message={
          <span>
            آیا از حذف <strong>«{toDelete?.nameFa}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف تعطیلی"
        confirmColor="error"
        loading={saving}
        onCancel={closeDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default HolidayManagement;
